import Spacer from "@components/Spacer";
import FacebookIcon from "@components/icons/facebook-icon";
import InstagramIcon from "@components/icons/instagram-icon";
import { QualityGreenStars, QualityMarkEko } from "@components/quality-mark";
import ReceiveNewsletter from "@components/receive-newsletter";
import { useAuth } from "@provider/context-auth";
import { A, H3, Text, View } from "dripsy";
import * as React from "react";
import InstallPWA from "@features/pwa/install-pwa";
import type { FooterStoryblok } from "@type/storyblock-types";
import { useStoryBlokStory } from "@provider/storyblok-provider";
import ButtonLink from "@components/button-link";
import routes from "../NavigatorRoutes";

const bodyFontSize = [15, 16, 17];
function Footer() {
  const { isAuthenticated } = useAuth();

  const content = useStoryBlokStory<FooterStoryblok>("footer");

  return (
    <View
      sx={{
        backgroundColor: "$secondaryBackground",
        borderTopWidth: 1,
        borderTopColor: "$secondaryBackgroundDarker",
        marginTop: 50,
      }}
    >
      <View style={{ alignItems: "center", paddingTop: 24, paddingBottom: 24 }}>
        <InstallPWA />
      </View>
      <View
        sx={{
          flex: 1,
          flexDirection: ["column", "column", "row"],
          justifyContent: ["flex-start", "flex-start", "space-between"],
          maxWidth: "container",
          width: "100%",
          alignSelf: "center",
          padding: "$2",
          gap: 24,
        }}
      >
        <FooterColumn
          title={<FooterTitle>{isAuthenticated ? content.newsletter_title : content.social_title}</FooterTitle>}
        >
          {isAuthenticated && (
            <>
              <ReceiveNewsletter />
              <Spacer size="$4" />
            </>
          )}
          <FooterSocials instagram={content.social_instagram.url} facebook={content.social_facebook.url} />
        </FooterColumn>
        <FooterColumn title={<FooterTitle>{content.contact_title}</FooterTitle>}>
          <FooterLink href={`mailto:${content.contact_email}`}>{content.contact_email}</FooterLink>
          <FooterLink href={`tel:${content.contact_phone.replaceAll("-", "")}`}>{content.contact_phone}</FooterLink>
          <FooterLink
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(content.contact_adress)}`}
          >
            {content.contact_adress}
          </FooterLink>
          <Spacer size="$3" />
          <FooterLabelValue label="Kvk" value={content.contact_kvk} />
          <FooterLabelValue label="Btw" value={content.contact_tax} />
          <View sx={{ mt: 20, justifyContent: "flex-start", gap: 8 }}>
            <ButtonLink to={routes.TermsScreen} params={{}} color="$primary" px="$1" py="$1" outline>
              Algemene Voorwaarden
            </ButtonLink>
            <ButtonLink to={routes.PrivacyScreen} params={{}} color="$primary" px="$1" py="$1" outline>
              Privacy Verklaring
            </ButtonLink>
          </View>
        </FooterColumn>
        <FooterColumn title={<FooterTitle>{content.office_time_title}</FooterTitle>}>
          {content.office_times.map((officeItem) => (
            <FooterLabelValue key={officeItem._uid} label={officeItem.day} value={officeItem.time} />
          ))}
        </FooterColumn>
      </View>

      <View sx={{ alignItems: "center", flexDirection: "row", justifyContent: "center", marginVertical: 70 }}>
        <QualityMarkEko />
        <Spacer size="$3" horizontal />
        <QualityGreenStars />
      </View>
      <View style={{ alignItems: "center" }}>
        <A href="https://www.webridge.nl">
          <View sx={{ padding: "$2", alignItems: "center" }}>
            <Text>App ontwikkeld door </Text>
            <Text sx={{ fontWeight: "600", color: "$text" }}>webRidge</Text>
          </View>
        </A>
      </View>
    </View>
  );
}

const FooterSocials = ({ instagram, facebook }: { instagram: string; facebook: string }) => {
  return (
    <View sx={{ flexDirection: "row" }}>
      <A href={instagram} style={{ marginRight: 12 }}>
        <InstagramIcon size={40} color="$text" />
      </A>
      <A href={facebook}>
        <FacebookIcon size={40} color="$text" />
      </A>
    </View>
  );
};

export function FooterColumn({ title, children }: { title: any; children: any }) {
  return (
    <View style={{ flex: 1, minHeight: "auto" }}>
      {title}
      <Spacer size="$1" />
      {children}
    </View>
  );
}

const FooterTitle = ({ children }: { children: string }) => {
  return (
    <H3
      sx={{
        fontWeight: "700",
        color: "$text",
        fontSize: [18, 19, 22],
        marginBottom: "$2",
      }}
    >
      {children}
    </H3>
  );
};

const FooterLabelValue = ({ label, value }: { label: string; value: string }) => {
  return (
    <View sx={{ flexDirection: "row", alignItems: "center" }}>
      <Text sx={{ fontWeight: "700", color: "$text", width: 50, fontSize: bodyFontSize }}>{label}</Text>
      <Spacer size="$1" />
      <Text sx={{ color: "$text", fontSize: bodyFontSize }}>{value}</Text>
    </View>
  );
};

const FooterLink = ({ children, href }: { children: any; href?: string }) => {
  return (
    <A href={href} style={{ padding: 6, paddingLeft: 0, borderRadius: 10 }}>
      <Text sx={{ fontSize: bodyFontSize }}>{children}</Text>
    </A>
  );
};
export default Footer;
